import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { WHAiChatContextItem } from '@workheld/workheld-shared-lib';
import { filter, map, startWith } from 'rxjs';
import {
  IWHWorkObjectCrudStore,
  workheldWorkObjectCrudStoreFeatureKey,
} from 'src/app/app-stores/w-h-work-object-stores/w-h-work-object-crud-store/w-h-work-object-crud-store.reducer';
import { workheldWorkOrderCrudStoreFeatureKey } from 'src/app/app-stores/w-h-work-order-stores/w-h-work-order-crud-store/w-h-work-order-crud-store.reducer';
import { EquipmentStore } from '../equipment/equipment.store';

type Entity = { id?: string; extId?: string; title?: string; name?: string } | null | undefined;

/** Route params of the whole active route tree (child params win). */
function collectParams(route: ActivatedRouteSnapshot | null): Params {
  let params: Params = {};
  for (let current = route; current; current = current.firstChild) {
    params = { ...params, ...current.params };
  }
  return params;
}

/** Real ids only: create pages use 'NULL' / 'add'. */
function entityId(value: unknown): string | null {
  return typeof value === 'string' && value && value !== 'NULL' && value !== 'add' ? value : null;
}

function label(entity: Entity): string {
  return [entity?.extId, entity?.title ?? entity?.name].filter(Boolean).join(' · ');
}

/**
 * What the user is looking at, as AI chat page context: the opened work
 * order, work object or equipment. Null until the entity is loaded, so the
 * chip never shows a bare id.
 */
@Injectable({ providedIn: 'root' })
export class WHAiPageContextService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly equipmentStore = inject(EquipmentStore);

  private readonly params = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => collectParams(this.router.routerState.snapshot.root)),
    ),
    { initialValue: {} as Params },
  );
  private readonly workObjectState = toSignal(
    this.store.select<IWHWorkObjectCrudStore | undefined>(workheldWorkObjectCrudStoreFeatureKey as any),
  );
  private readonly workOrder = toSignal(this.store.select<Entity>(workheldWorkOrderCrudStoreFeatureKey as any));

  readonly pageContext = computed<WHAiChatContextItem | null>(
    () => this.workOrderContext() ?? this.workObjectContext() ?? this.equipmentContext(),
    // Same entity and label: keep the object, so the chat doesn't re-add a dismissed chip
    { equal: (a, b) => a?.id === b?.id && a?.label === b?.label },
  );

  private workOrderContext(): WHAiChatContextItem | null {
    const id = entityId(this.params()['workOrderId']);
    const workOrder = this.workOrder();
    if (!id || workOrder?.id !== id || !label(workOrder)) {
      return null;
    }
    return {
      id: `workOrder:${id}`,
      type: 'workOrder',
      entityId: id,
      label: label(workOrder),
      data: { workObjectId: entityId(this.params()['workObjectId']) },
    };
  }

  private workObjectContext(): WHAiChatContextItem | null {
    const id = entityId(this.params()['workObjectId']);
    const state = this.workObjectState();
    // The crud store keeps one DOM per work object type; take the one that is open
    const workObject = [state?.projectDOM, state?.caseDOM, state?.standingOrderDOM, state?.jobDOM].find(
      (dom: Entity) => !!id && dom?.id === id,
    ) as Entity;
    if (!id || !workObject || !label(workObject)) {
      return null;
    }
    return { id: `workObject:${id}`, type: 'workObject', entityId: id, label: label(workObject) };
  }

  private equipmentContext(): WHAiChatContextItem | null {
    const nodeId = entityId(this.params()['nodeId']);
    const node = this.equipmentStore.currentNode();
    if (!nodeId || node?.id !== nodeId || !label(node as Entity)) {
      return null;
    }
    return {
      id: `equipment:${nodeId}`,
      type: 'equipment',
      entityId: nodeId,
      label: label(node as Entity),
      data: { equipmentId: this.equipmentStore.equipment()?.id },
    };
  }
}
